/* global analytics */
const { apiFetch, i18n } = wp;
const { __ } = i18n;

/**
 * Gets the localized config for blog navigation.
 *
 * @type {object}
 */
const getBlogNavigationConfig = () => {

	const {
		MSRData: {
			blogNavigation
		},
	}  = window;

	return blogNavigation;
};

/**
 * Returns the element ID for the wrapper.
 *
 * @return {string}
 */
const getWrapperElementId = () => {

	const {
		wrapper,
	} = getBlogNavigationConfig();

	return wrapper;
};

/**
 * build URL
 * @param researchArea
 * @param page
 * @returns {string}
 */
const buildURL = ( researchArea, page ) => {
	const {
		categories,
		eventTypes,
		eventTypeTaxonomy,
		endpointUrl,
		search,
	} = getBlogNavigationConfig();

	let url;
	if ( endpointUrl.includes( '?' ) ) {
		url = `${endpointUrl}&`;
	} else {
		url = `${endpointUrl}?`;
	}

	const paged = 1 < page ? page : 1;
	const perPage = 12;
	const podcastCat = window.MSR_content_refs['msr-podcast'];

	url = `${url}&per_page=${perPage}&page=${paged}`;

	if ( 'all' !== researchArea ) {
		url += `&research-area=${researchArea}`;
	}

	if ( categories ) {
		url += `&categories=${categories}`;
	} else {
		url += `&categories_exclude=${podcastCat}`;
	}

	if ( eventTypes ) {
		url += `&${eventTypeTaxonomy}=${eventTypes}`;
	}

	if ( search ) {
		url += `&search=${search}`;
	}

	return url;
};

/**
 * Paginate
 * @param event
 * @returns {Promise<void>}
 */
const changePage = async ( event ) => {
	event.preventDefault();

	if ( ! event.target.getAttribute( 'data-page' ) ) {
		return;
	}

	const wrapper = document.getElementById( getWrapperElementId() );
	wrapper.style.opacity = 0.5;

	const { page } = event.target.dataset;
	const url = buildURL( window.MSR.blogHomepage.currentResearchArea, page );
	const posts = await getPosts( url );

	updateState( {
		currentPage: parseInt( page ),
		populateGridRan: false,
	} );

	const grid = populateGrid( posts );

	printGrid( grid );
	printPagination();

	document.querySelector( '#post-archive-grid .card:first-of-type a' ).focus();

	wrapper.style.opacity = 1;
};

/**
 * Changes the active research area for the post grid based on a click event.
 * @param {Event} e The change event for the research area dropdown.
 */
async function changeArea( e ) {
	e.preventDefault();

	const wrapper = document.getElementById( getWrapperElementId() );
	const dropdown = e.currentTarget;
	const researchAreaTermId = dropdown.value;

	const { innerText: researchArea } = dropdown.options[dropdown.selectedIndex];

	// Fire analytics events.
	document.querySelector( 'meta[name="awa-pgtop"]' ).setAttribute( 'content', researchArea );
	if ( window.awa ) {
		analytics.captureContentUpdate();
	}

	wrapper.style.opacity = 0.5;

	// Fetch the posts.
	const page = 1;
	const url = buildURL( researchAreaTermId, page );
	const posts = await getPosts( url );
	const state = {
		currentPage: parseInt( page ),
		currentResearchArea: researchAreaTermId,
		populateGridRan: false,
	};
	updateState( state );

	// Update DOM.
	const grid = populateGrid( posts );
	printGrid( grid );
	printPagination();

	// Un-fade the wrapper.
	wrapper.style.opacity = 1;
}

/**
 * fetch posts
 * @param url
 */
const getPosts = async ( url ) => {
	let posts;

	await apiFetch( { url, parse: false } )
		.then( async ( response ) => {
			const { headers } = response;
			posts = response.json();

			const state = {
				totalPosts: parseInt( headers.get( 'x-wp-total' ) ),
				totalPages: parseInt( headers.get( 'x-wp-totalpages' ) )
			};

			updateState( state );

		} );

	return posts;
};

/**
 * populate grid
 * @param posts
 * @returns {string}
 */
const populateGrid = ( posts ) => {

	const {
		templateId,
	} = getBlogNavigationConfig();

	const {
		currentPage,
		promo,
		populateGridRan,
	} = getState();

	const templateNode = document.getElementById( templateId );
	let grid = '';
	grid = `<div class="container">
				<ol class="row row-cols-1 row-cols-md-2 row-cols-lg-3 list-unstyled">`;

	let i = 0;

	posts.forEach( function( post ) {

		let skipPost = false;

		// If this is page one and it's the first time running this function, skip everything after the third item.
		// If this is page one and it's the second time running this function, skip the first three items.
		if ( promo && 1 === currentPage ) {
			if ( ! populateGridRan && 2 < i || populateGridRan && 2 >= i ) {
				skipPost = true;
			}
		}

		if ( ! skipPost ) {
			grid += '<div class="col my-3">';
			grid += populateTemplate( templateNode, post );
			grid += '</div>';
		}

		i++;
	} );

	grid += '</ol></div>';

	if ( promo && 1 === currentPage && ! populateGridRan ) {

		// Insert the promo markup.
		grid += promo;

		// Let the function know it already ran once, then run it again to insert the rest of the items.
		updateState( { populateGridRan: true } );
		grid += populateGrid( posts );
	}

	return grid;
};

/**
 * Populates the template based on the post data.
 *
 * @param  {object} templateNode The template element.
 * @param  {object} post         The post data.
 * @return {string}
 */
const populateTemplate = ( templateNode, post ) => {

	const {
		endpointUrl,
	} = getBlogNavigationConfig();

	let template = templateNode.innerHTML;

	let postExcerpt = '';

	if ( post.event_excerpt ) {
		postExcerpt = post.event_excerpt;
	} else if ( post.formattedExcerpt ) {
		postExcerpt = post.formattedExcerpt;
	}

	let dateFormat = 'F j, Y';
	if ( endpointUrl.toLowerCase().includes( 'locale=zh_cn' ) || endpointUrl.toLowerCase().includes( 'locale=zh-cn' ) ) {
		dateFormat = 'Y年n月j日';
	}

	if ( post.date ) {
		const postDate = new Date( post.date );
		const formattedDate = dateFormat
			.replace( 'Y', postDate.getFullYear( ) )
			.replace( 'n', postDate.getMonth() + 1 )
			.replace( 'j', postDate.getDate() )
			.replace( 'F', new Intl.DateTimeFormat( 'en-US', { month: 'long' } ).format( postDate ) );
		template = template.replace( /{%- postDate %}/g, formattedDate );
	}

	template = template.replace( /{%- hiddenClass %}/g, post.hiddenClass ? post.hiddenClass : '' );
	template = template.replace( /{%- postTitle %}/g, post.title.rendered );
	template = template.replace( /{%- postExcerpt %}/g, postExcerpt );
	template = template.replace( /{%- postByline %}/g, post.byline ? `| ${post.byline}` : '' );
	template = template.replace( /{%- postImage %}/g, post.featured_image_thumbnail || '' );
	template = template.replace( /{%- imageAnchorClass %}/g, ! post.featured_image_thumbnail ? 'hidden' : '' );
	template = template.replace( /{%- postPermalink %}/g, post.link );
	template = template.replace( /{%- postId %}/g, post.id );
	template = template.replace( /{%- postType %}/g, post.type );
	template = template.replace( /{%- podcastEpisode %}/g, post.podcast_episode ? post.podcast_episode : '' );
	template = template.replace( /{%- startDateFormatted %}/g, post.msr_startdate_formatted ? post.msr_startdate_formatted : '' );
	template = template.replace( /{%- eventTime %}/g, post.msr_event_time ? post.msr_event_time : '' );
	template = template.replace( /{%- ctaLink %}/g, post.msr_cta_link ? post.msr_cta_link : '' );
	template = template.replace( /{%- ctaText %}/g, post.msr_cta_text ? post.msr_cta_text : '' );
	template = template.replace( /{%- ctaBiCn %}/g, post.msr_cta_bi_name ? post.msr_cta_bi_name : '' );
	template = template.replace( /{%- maybeShowCTA %}/g, '' === post.msr_cta_link ? ' d-none' : '' );

	return template;
};

/**
 * print Grid data
 * @param grid
 */
const printGrid = ( grid ) => {
	const wrapper = document.getElementById( getWrapperElementId() );
	wrapper.innerHTML = grid;
};

/**
 * print pagination controls
 */
const printPagination = () => {
	const state = getState();
	const wrapper = document.getElementById( getWrapperElementId() );
	let markup = '';
	let prev, next, current, prevNext;

	if ( 'totalPages' in state && 2 > parseInt( state.totalPages ) ) {
		wrapper.innerHTML += markup;
		refresh();
		return;
	}

	markup += `
				<div class="mb-5">
					<nav id="post-archive-pagination"
						aria-label="${__( 'Pagination Navigation', 'ms-research' )}"
					>
						<ul class="pagination justify-content-center">
				`;

	// Display the Previous link if we're past page one
	if ( 1 < state.currentPage ) {
		prev = state.currentPage - 1;
		prevNext = __( 'Previous', 'ms-research' );
		markup += getPaginationLinkMarkup( prev, prevNext, state.currentPage );
	}

	prevNext = false;

	// Display the Page 1 link if we're past page 5
	if ( 1 < state.currentPage - 4 ) {
		markup += getPaginationLinkMarkup( 1, prevNext, current );

		// Follow that with 3 dots if we're past page 6
		if ( 1 < state.currentPage - 5 ) {
			markup += getPaginationLinkMarkup( 'dots', prevNext, current );
		}
	}


	for ( let i = 1; i <= state.totalPages; i++ ) {
		current = false;
		prevNext = false;

		if ( i === state.currentPage ) {
			current = true;
		}

		if ( 5 < state.totalPages && ( i < state.currentPage - 4 || i > state.currentPage + 2 ) ) {
			continue;
		}

		markup += getPaginationLinkMarkup( i, prevNext, current );
	}

	// Display the last page link if we're still 2 or more pages behind
	if ( state.currentPage < state.totalPages - 2 && 5 < state.totalPages ) {
		// Preceed it with 3 dots if 3 or more pages behind
		if ( state.currentPage < state.totalPages - 3 ) {
			markup += getPaginationLinkMarkup( 'dots', prevNext, current );
		}
		markup += getPaginationLinkMarkup( state.totalPages, prevNext, current );
	}

	// Display the Next page link if we're not at the last page
	if ( state.currentPage < state.totalPages ) {
		next = state.currentPage + 1;
		prevNext = __( 'Next', 'ms-research' );
		markup += getPaginationLinkMarkup( next, prevNext, state.currentPage );
	}

	markup += '</ul></nav></div>';

	wrapper.innerHTML += markup;

	refresh();
};

/**
 * Build link markup
 * @param page
 * @param prevNext
 * @param current
 * @returns {string}
 */
const getPaginationLinkMarkup = ( page, prevNext, current ) => {
	const state = getState();
	const regularPageMarkup = '<a class="page-link" aria-label="{%- pageAriaLabel %} {%- page %}" data-page="{%- page %}" data-bi-cN="Page {%- page %}" data-bi-id="" href="{%- basePaginationUrl %}{%- page %}/{%- researchAreaQuery %}">{%- page %}</a>';
	const currentPageMarkup = '<a aria-current="page" class="page-link"><span>{%- page %}</span></a>';
	const prevNextPageMarkup = '<a class="page-link" aria-label="{%- prevNextAriaLabel %} {%- prevNext %}" data-page="{%- page %}" data-bi-cN="{%- prevNext %}" data-bi-id="" href="{%- basePaginationUrl %}{%- page %}/{%- researchAreaQuery %}">{%- prevNext %}</a>';
	const dotsMarkup = '<span class="page-link text-body">&hellip;</span>';

	const regularLinkWrapperMarkup = '<li class="page-item">{%- link %}</li>';
	const currentLinkWrapperMarkup = '<li class="page-item active" aria-current="page">{%- link %}</li>';
	const ellipsisWrapperMarkup = '<li class="page-item disabled ellipsis">{%- link %}</li>';

	let markup;

	if ( prevNext ) {
		markup = prevNextPageMarkup.replace( /{%- prevNext %}/g, prevNext );
		markup = markup.replace( /{%- page %}/g, page );
		markup = markup.replace( /{%- prevNextAriaLabel %}/g, __( 'Link to', 'ms-research' ) );
	} else if ( current ) {
		markup = currentPageMarkup.replace( /{%- page %}/g, page );
	} else if ( 'dots' === page ) {
		markup = dotsMarkup;
	} else {
		markup = regularPageMarkup.replace( /{%- page %}/g, page );
		markup = markup.replace( /{%- pageAriaLabel %}/g, __( 'Link to page', 'ms-research' ) );
	}

	// TODO this may not be being passed?
	if ( 'all' !== state.currentResearchArea ) {
		const researchArea = `research-area=${state.currentResearchArea}`;
		markup = markup.replace( /{%- researchAreaQuery %}/, researchArea );
	} else {
		markup = markup.replace( /{%- researchAreaQuery %}/, '' );
	}

	const {
		basePaginationUrl,
	} = getBlogNavigationConfig();

	markup = markup.replace( /{%- basePaginationUrl %}/, basePaginationUrl );

	if ( current && !prevNext ) {
		markup = currentLinkWrapperMarkup.replace( /{%- link %}/g, markup );
	} else if ( 'dots' === page ) {
		markup = ellipsisWrapperMarkup.replace( /{%- link %}/g, markup );
	} else {
		markup = regularLinkWrapperMarkup.replace( /{%- link %}/g, markup );
	}

	return markup;
};

/**
 * get the state
 * @returns {{blogHomepage: *}}
 */
const getState = () => {
	return window.MSR.blogHomepage;
};

/**
 * Updates the state
 * @param obj
 */
const updateState = ( newState ) => {
	let state = getState();
	state = {
		...state,
		...newState
	};

	window.MSR.blogHomepage = state;
};

/**
 * Reinstate the necessary event listeners after the content has changed
 */
const refresh = () => {
	const paginationControls = document.getElementById( 'post-archive-pagination' );
	if ( paginationControls ) {
		paginationControls.addEventListener( 'click', changePage );
	}
};

/**
 * Init Blog Navigation
 */
const init = () => {

	const selectControl = document.getElementById( 'blog-navigation-select' );
	const paginationControls = document.getElementById( 'post-archive-pagination' );

	if ( !selectControl && !paginationControls ) {
		return false;
	}

	let promo = null;
	const promoElement = document.querySelector( '.msr-blog .msr-blog-cta' );
	if ( promoElement )  {
		promo = promoElement.outerHTML;
	}

	const state = {
		totalPosts: 0,
		totalPages: 1,
		currentPage: 1,
		currentResearchArea: 'all',
		promo,
	};
	updateState( state );

	if ( selectControl ) {
		selectControl.addEventListener( 'change', changeArea );
	}

	if ( paginationControls ) {
		paginationControls.addEventListener( 'click', changePage );
	}

};

// Exports
export default init;
